/**
 * Copyright (C) 2019 Egylis,
 * Siège social, 83 bis rue de l'Abbé Groult, 75015 Paris - France.
 * All Rights Reserved.
 *
 * E-mail : info@egylis.com
 *
 * This software is the confidential and proprietary information
 * of Egylis. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only
 * in accordance with the terms of the license agreement you
 * entered into with Egylis.
 *
 * Created by calvez on 16/07/2019.
 *
 * Version : Generator 2.0.9
 */

import {Inject, Injectable} from '@angular/core';
import {enGB, fr, de} from 'date-fns/locale';
import {TranslateService} from '@ngx-translate/core';
import {DOCUMENT, registerLocaleData} from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeEn from '@angular/common/locales/en';
import localeDe from '@angular/common/locales/de';
import localeFrExtra from '@angular/common/locales/extra/fr';
import localeEnExtra from '@angular/common/locales/extra/en';
import localeDeExtra from '@angular/common/locales/extra/de';

/**
 * I18n Service
 */
@Injectable()
export class MoI18nService {

    /**
     * Default Locale Lang
     */
    private static _DEFAULT_LANG: string = 'fr';
    /**
     * Default Date Locale
     */
    private static _DEFAULT_DATE_LOCALE: Locale = fr;
    /**
     * Default L Locale
     */
    private static _DEFAULT_FORMAT_L_LOCALE: string = 'dd/MM/yyyy';
    /**
     * Default LLLL Locale
     */
    private static _DEFAULT_FORMAT_LLLL_LOCALE: string = 'dddd D MMMM yyyy HH:mm';
    /**
     * Private Date.
     * @private
     */
    private static _I18N_DATES = {
        weekdays: ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'],
        months  : ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aou', 'Sep', 'Oct', 'Nov', 'Déc'],
    };


    /**
     * Getter.
     * @return {Object}
     * @constructor
     */
    public static get DEFAULT_DATE_LOCALE(): Locale {
        return this._DEFAULT_DATE_LOCALE;
    }

    /**
     * Getter.
     * @return {string}
     * @constructor
     */
    public static get DEFAULT_FORMAT_L_LOCALE(): string {
        return this._DEFAULT_FORMAT_L_LOCALE;
    }

    /**
     * Getter.
     * @return {string}
     * @constructor
     */
    public static get DEFAULT_FORMAT_LLLL_LOCALE(): string {
        return this._DEFAULT_FORMAT_LLLL_LOCALE;
    }

    /**
     * Getter
     * @constructor
     */
    public static get DEFAULT_LANG(): string {
        return this._DEFAULT_LANG;
    }

    /**
     * Getter.
     * @return {}
     * @constructor
     */
    public static get I18N_DATES(): {
        weekdays: (string | string | string | string | string | string | string)[];
        months: (string | string | string | string | string | string | string | string | string | string | string | string)[]
    } {
        return this._I18N_DATES;
    }

    /**
     * Constructor
     * @param translate
     * @param document
     */
    constructor(protected translate: TranslateService,
                @Inject(DOCUMENT) private document: Document) {
        // this language will be used as a fallback when a translation isn't found in the current language
        translate.setDefaultLang('fr');

        // the lang to use, if the lang isn't available, it will use the current loader to get them
        translate.use('fr');
        registerLocaleData(localeFr, 'fr', localeFrExtra);
        if (this.document != null) {
            this.document.documentElement.lang = MoI18nService._DEFAULT_LANG;
        }
    }


    /**
     * Set Langs for RRules
     * @param lang
     */
    public setLang(lang: string): void {
        switch (lang) {
            case 'EN':
            case 'en':
                MoI18nService._DEFAULT_LANG = 'en';
                this.translate.use('en');
                registerLocaleData(localeEn, 'en', localeEnExtra);
                MoI18nService._DEFAULT_DATE_LOCALE = enGB;
                MoI18nService._DEFAULT_FORMAT_L_LOCALE = 'MM/dd/yyyy';
                MoI18nService._DEFAULT_FORMAT_LLLL_LOCALE = 'dddd, MMMM D, yyyy h:mm A';
                MoI18nService._I18N_DATES = {
                    weekdays: ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'],
                    months  : ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aou', 'Sep', 'Oct', 'Nov', 'Déc'],
                };
                break;
            case 'FR':
            case 'fr':
                MoI18nService._DEFAULT_LANG = 'fr';
                this.translate.use('fr');
                registerLocaleData(localeFr, 'fr', localeFrExtra);
                MoI18nService._DEFAULT_DATE_LOCALE = fr;
                MoI18nService._DEFAULT_FORMAT_L_LOCALE = 'dd/MM/yyyy';
                MoI18nService._DEFAULT_FORMAT_LLLL_LOCALE = 'dddd D MMMM yyyy HH:mm';
                MoI18nService._I18N_DATES = {
                    weekdays: ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'],
                    months  : ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aou', 'Sep', 'Oct', 'Nov', 'Déc'],
                };
                break;
            case 'DE':
            case 'de':
                MoI18nService._DEFAULT_LANG = 'de';
                this.translate.use('de');
                registerLocaleData(localeDe, 'de', localeDeExtra);
                MoI18nService._DEFAULT_DATE_LOCALE = de;
                MoI18nService._DEFAULT_FORMAT_L_LOCALE = 'dd.MM.yyyy';
                MoI18nService._DEFAULT_FORMAT_LLLL_LOCALE = 'dddd D MMMM yyyy HH:mm';
                MoI18nService._I18N_DATES = {
                    weekdays: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
                    months  : ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
                };
                break;
        }
        if (this.document != null) {
            this.document.documentElement.lang = MoI18nService._DEFAULT_LANG;
        }
    }

}
